import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  ProgressBar,
  Row,
} from "react-bootstrap";
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  UseFormWatch,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { load } from "recaptcha-v3";
import dashboardico from "../../assets/images/gotodashboard.svg";
import savedraftico from "../../assets/images/save-draft.svg";
import {
  getAllSurveyResponses,
  getPollResponse,
  getSurveySectionsDetails,
  getUserData,
  insertSurveyResponse,
  insertSurveyResponseViewDateTime,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import {
  convertToUTC,
  decrypt,
  getCurrentDate,
  hhmmToSeconds,
} from "../../utils/common";
import {
  showToast,
  ToastTypeCode
} from "../../utils/Constant";
import { AppConfig } from "../../AppConfig";
import OrganizationLogo from "../header/OrganizationLogo";
import Loader from "../loader/Loader";
import WelcomePage from "../pages/WelcomePage";
import NotAcceptingForm from "../pages/NotAcceptingForm";
import "./DetailSurveyForm.scss";
import PollResult from "./PollResult";
import PollView from "./PollView";
import RecursiveDetailSurveyForm from "./RecursiveDetailSurveyForm";
import {
  DetailedInfo,
  SurveyById,
  SurveyResponse,
  SurveySection,
} from "./types";

const DetailSurveyForm: React.FC = () => {
  const [applyValidation, setApplyValidation] = useState(true);
  const [isNotAccepting, setIsNotAccepting] = useState(false);
  const [showSubmitButton, setShowSubmitButton] = useState(true);
  const [showProgress, setShowProgress] = useState(true);
  const [responseStartDate, setResponseStartDate] = useState<Date | null>(null);
  const [responseEndDate, setResponseEndDate] = useState<Date | null>(null);
  let { id, surveyAssignmentId, assignUserId, surveyIdFromParams } = useParams();
  const [currentCount, setCurrentCount] = useState<number>(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const methods = useForm({
    mode: "onChange",
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    watch,
    getValues,
    register,
  } = methods;

  const [surveySections, setSurveySections] = useState<SurveySection[]>([]);
  const [surveyFormTitle, setSurveyFormTitle] = useState<string>("");
  const [thankYouMessage, setThankYouMessage] = useState<string>("");
  const [surveyFormInstructions, setSurveyFormInstructions] =
    useState<string>("");
  const [surveyDataById, setSurveyDataById] = useState<SurveyById[]>([]);
  const navigate = useNavigate();

  // Detect if the survey is loaded inside an iframe (embedded on a 3rd-party website)
  const isEmbedded = useMemo(() => {
    try {
      // window.self !== window.top is true when inside any iframe
      const isInIframe = window.self !== window.top;
      // Also support explicit ?embed=true query parameter
      const isEmbed = new URLSearchParams(window.location.search).get("embed") === "true";
      return isInIframe || isEmbed;
    } catch (e) {
      // If accessing window.top throws (cross-origin), we're definitely in an iframe
      return true;
    }
  }, []);
  const [surveysFromId, setSurveysFromId] = useState<any>();
  const [surveysAssignmentId, setSurveysAssignmentId] = useState<number>();
  const [userId, setUserId] = useState<any>();
  const [surveyId, setSurveyId] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(0);
  const [welcomePageDemo, setWelcomePageDemo] = useState(false);
  const [pollResultView, setPollResultView] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [isTimeOver, setIsTimeOver] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [durationInMinutes, setDurationInMinutes] = useState<number>();
  const [alertBeforeTime, setAlertBeforeTime] = useState<number>();
  const [formType, setFormType] = useState<number>(1);
  const [timeLeft, setTimeLeft] = useState<number>();
  const [submitButtonClicked, setSubmitButtonClicked] = useState<number>(1);
  const [timeDifferenceAgainLogin, setTimeDifferenceAgainLogin] =
    useState<number>(0);
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState<boolean>(false);
  const [totalquestions, setTotalQuestions] = useState<number>(0);
  const [isPublicSurvey, setIsPublicSurvey] = useState(false);
  const [orgId, setOrgId] = useState<number>();
  const watchedValues = watch();

  const clearDraft = () => {
    if (surveyId && userId) {
      const storageKey = `survey_draft_${surveyId}_${userId}`;
      localStorage.removeItem(storageKey);
    }
  };

  const handleCaptcha = async (): Promise<string | null> => {
    try {
      const recaptcha = await load(
        AppConfig.RECAPTCHA_SITE_KEY as string
      );
      const resultToken = await recaptcha.execute("formSubmission");
      return resultToken;
    } catch (error) {
      console.error("reCAPTCHA error:", error);
      return null;
    }
  };

  const [isKeyRequired, setIsKeyRequired] = useState<boolean>(false);
  const [showKeyModal, setShowKeyModal] = useState<boolean>(false);
  const [accessKey, setAccessKey] = useState<string>("");
  const [isKeyValidated, setIsKeyValidated] = useState<boolean>(false);
  const [keyError, setKeyError] = useState<string>("");

  const fetchAllData = async () => {
    let keyReq = false;
    const query = new URLSearchParams(window.location.search);
    const isKeyEnabledEncrypted = query.get("isKeyEnabled");
    if (isKeyEnabledEncrypted) {
      try {
        const decryptedKeyEnabled = await decrypt(isKeyEnabledEncrypted);
        if (String(decryptedKeyEnabled).toLowerCase() === "true") {
          keyReq = true;
          setIsKeyRequired(true);
        }
      } catch (e) {
        if (isKeyEnabledEncrypted.toLowerCase() === "true") {
          keyReq = true;
          setIsKeyRequired(true);
        } else {
          console.error("Error processing key flag");
        }
      }
    }

    let urlUserId = assignUserId ? await decrypt(assignUserId) : "";
    let finalUserId = urlUserId;
    let finalUserOrgId = null;

    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const UserDataFromToken: any = await axiosPost(getUserData, null);
        if (UserDataFromToken?.ErrorMessage == null && UserDataFromToken?.data?.ExtraData?.UserId) {
          finalUserId = UserDataFromToken.data.ExtraData.UserId;
          finalUserOrgId = UserDataFromToken.data.ExtraData.OrganizationId;
        }
      } catch (e) {
        console.error("Error fetching user data", e);
      }
    }

    setUserId(finalUserId);

    let isPublic = false;
    let assignmentKeyEnabled = false;

    // Validate Organization, End Date, and check if Key is required dynamically for logged in users
    if (surveyAssignmentId) {
      const assignmentIdStr = await decrypt(surveyAssignmentId);
      if (token && finalUserOrgId) {
        try {
          const response: any = await axiosPost("/SurveyAssignments/GetSurveyAssignmentsDataById", {
            SurveyAssignmentId: Number(assignmentIdStr)
          });
          if (response?.data?.Status === 200) {
            const assignment = response?.data?.ExtraData?.SurveyAssignments?.[0];
            if (assignment) {
              isPublic = assignment.IsPublicSurvey;
              setOrgId(assignment.OrganizationId);
              
              if (assignment.IsEnrollmentKeyEnabled === true || assignment.IsEnrollmentKeyEnabled === 1) {
                assignmentKeyEnabled = true;
              }

              if (assignment.OrganizationId !== finalUserOrgId) {
                showToast("You are not authorized to access this survey.", ToastTypeCode.ERROR);
                navigate("/");
                return;
              }

              // Use local date comparison to avoid timezone shift issues
              const currentDate = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD format
              const targetCompletionDate = new Date(assignment.TargetCompletionDate).toLocaleDateString("en-CA");
              if (targetCompletionDate < currentDate) {
                setIsNotAccepting(true);
              }
            }
          }
        } catch (e) {
          console.error("Error fetching assignment data", e);
        }
      } else if (!keyReq) {
        // Public without key — for public surveys, isPublic is always true
        isPublic = true;
        try {
          const surveyIdStr = surveyIdFromParams ? await decrypt(surveyIdFromParams) : "";
          if (surveyIdStr && surveyIdStr !== "-1") {
            const response: any = await axiosPost("/Surveys/GetSurveysById", {
              SurveyId: Number(surveyIdStr)
            });
            if (response?.data?.Status === 200) {
              const survey = response?.data?.ExtraData;
              if (survey) {
                // Use local date comparison to avoid timezone shift issues
                const currentDate = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD format
                const targetDate = new Date(survey.TargetCompletionDate).toLocaleDateString("en-CA");
                if (targetDate < currentDate) {
                  setIsNotAccepting(true);
                }
              }
            }
          }
        } catch (e) {
          console.error("Error fetching survey data", e);
        }
      }
    }

    if (assignmentKeyEnabled) {
      keyReq = true;
      setIsKeyRequired(true);
    }

    const storageKeyForAuth = `validated_key_for_${id}_${finalUserId}`;
    let validatedStatus = sessionStorage.getItem(storageKeyForAuth);
    let hasValidatedSession = !!validatedStatus;

    // Consume generic key if it exists (from pre-login validation)
    if (!hasValidatedSession) {
      const genericKey = `validated_key_for_${id}`;
      validatedStatus = sessionStorage.getItem(genericKey);
      if (validatedStatus) {
        if (token) {
          sessionStorage.setItem(storageKeyForAuth, validatedStatus);
          sessionStorage.removeItem(genericKey);
          hasValidatedSession = true;
        } else {
          hasValidatedSession = true;
        }
      }
    }

    if (hasValidatedSession) {
      setIsKeyValidated(true);
    }

    if (keyReq && !hasValidatedSession && !isKeyValidated) {
      setShowKeyModal(true);
      return;
    }

    // If key is required, and it's a private survey, login is ALWAYS required
    if (keyReq && !token && validatedStatus !== "public") {
      navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    
    // Fallback: If it's a private survey without key, login is STILL required
    if (!token && !isPublic && !keyReq) {
      navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }

    setIsPublicSurvey(isPublic);

    await fetchData(finalUserOrgId, finalUserId);
  };

  const validateAccessKey = async () => {
    try {
      setLoading(true);
      const surveyFormIdStr = await decrypt(id);
      const surveyAssignmentIdStr = surveyAssignmentId ? await decrypt(surveyAssignmentId) : "0";
      const response: any = await axiosPost("/Surveys/ValidateAccessKey", {
        SurveyAssignmentId: Number(surveyAssignmentIdStr),
        SurveyFormId: Number(surveyFormIdStr),
        AccessKey: accessKey
      });

      if (response?.data?.Status === 200 && response?.data?.ExtraData?.IsValid === true) {
        const extraData = response.data.ExtraData;
        const token = localStorage.getItem("accessToken");
        
        const statusToStore = extraData.IsPublicSurvey ? "public" : "private";

        if (token) {
          sessionStorage.setItem(`validated_key_for_${id}_${userId}`, statusToStore);
        } else {
          sessionStorage.setItem(`validated_key_for_${id}`, statusToStore);
        }

        setIsKeyValidated(true);
        setShowKeyModal(false);

        // Key enabled means login is required for non-public surveys
        if (!token && !extraData.IsPublicSurvey) {
          navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        }
      } else {
        setKeyError("Invalid Access Key. Please try again.");
      }
    } catch (e) {
      setKeyError("Error validating access key.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [id, assignUserId, surveyIdFromParams, isKeyValidated]);

  useEffect(() => {
    if (!welcomePageDemo && showSubmitButton && formType === 1) return;
    if (
      formType === 2 &&
      welcomePageDemo &&
      showSubmitButton === true &&
      userId &&
      surveyId
    ) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime = 0) => {
          if (prevTime <= 1) {
            setIsTimeOver(true);
            setValue("TimeOver", true);
            clearDraft();
            clearInterval(timer);
            handleSubmit(handleFinalSubmit)();
            navigate("/time-over");
            return 0;
          }
          if (alertBeforeTime !== undefined && prevTime === alertBeforeTime) {
            showToast(
              `Hurry up! Only ${alertBeforeTime / 60} minutes left.`,
              ToastTypeCode?.ERROR
            );
          }
          return prevTime - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [navigate, welcomePageDemo, formType]);

  useEffect(() => {
    if (welcomePageDemo === true) {
      if (!isPublicSurvey && surveyId) {
        const response: any = axiosPost(insertSurveyResponseViewDateTime, {
          SurveyId: surveyId,
        });
      }
      setResponseStartDate((prev) => prev || new Date());
    }
  }, [welcomePageDemo, isPublicSurvey, surveyId]);

  // Persistence for public surveys
  useEffect(() => {
    if (isPublicSurvey && surveyId && userId && welcomePageDemo) {
      const storageKey = `survey_draft_${surveyId}_${userId}`;
      const draftData = {
        formData: watchedValues,
        responseStartDate: responseStartDate?.toISOString(),
        welcomePageDemo: welcomePageDemo
      };
      localStorage.setItem(storageKey, JSON.stringify(draftData));
    }
  }, [watchedValues, responseStartDate, welcomePageDemo, isPublicSurvey, surveyId, userId]);

  useEffect(() => {
    const restoreData = async () => {
      if (isPublicSurvey && surveyId && userId) {
        const storageKey = `survey_draft_${surveyId}_${userId}`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          try {
            const { formData, responseStartDate: savedStartDate, welcomePageDemo: savedWelcomePage } = JSON.parse(saved);

            if (formData) {
              Object.entries(formData).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                  setValue(key as any, value);
                }
              });
            }

            if (savedStartDate) {
              const startDate = new Date(savedStartDate);
              setResponseStartDate(startDate);

              const now = new Date();
              const diffInSeconds = Math.floor((now.getTime() - startDate.getTime()) / 1000);
              setTimeDifferenceAgainLogin(diffInSeconds);
            }

            if (savedWelcomePage) {
              setWelcomePageDemo(true);
            }
          } catch (e) {
            console.error("Error restoring draft", e);
          }
        }
      }
    };

    if (isPublicSurvey && surveyId && userId) {
      restoreData();
    }
  }, [isPublicSurvey, surveyId, userId]);

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handlePreview = () => {
    setIsDraft(true);
    setShowModal(false);
  };

  const fetchData = async (finalUserOrgId?: number | null, finalUserId?: any) => {
    if (id) {
      const survey = await decrypt(id);
      setSurveysFromId(survey);
    }

    if (surveyAssignmentId) {
      const decodedAssignment = await decrypt(surveyAssignmentId);
      setSurveysAssignmentId(Number(decodedAssignment));
    }

    if (assignUserId) {
      const user = await decrypt(assignUserId);
      setUserId(finalUserId || user);
    }

    if (surveyAssignmentId) {
      const decryptedAssignmentId = await decrypt(surveyAssignmentId);
      setSurveysAssignmentId(Number(decryptedAssignmentId));
    }

    if (!assignUserId) {
      setApplyValidation(false);
    }

    if (surveyIdFromParams) {
      const surveysid = await decrypt(surveyIdFromParams);
      setSurveyId(surveysid);
    }

    // Since we rely on ValidateAccessKey for private/key surveys to check org/dates,
    // and for public without key the backend handles it, we don't need a separate fetch here.
    return null;
  };

  const surveyFormId: any = surveysFromId;
  const usersId: any = userId;
  const surveyid: any = surveyId;

  const insertArrayAtIndex = <T,>(
    targetArray: T[],
    insertArray: T[],
    index: number
  ): T[] => {
    const resultArray = [...targetArray];
    resultArray?.splice(index, 0, ...insertArray);
    return resultArray;
  };

  const getOrderedDetailInfoRecursive = async (
    detailinfo: DetailedInfo[],
    orderedDetailinfo: DetailedInfo[],
    surveyBranchLogic: any[]
  ): Promise<DetailedInfo[]> => {
    if (!detailinfo || detailinfo?.length === 0) {
      return orderedDetailinfo;
    }

    const initialLength = detailinfo.length;
    let childQuestions: DetailedInfo[] = [];

    if (orderedDetailinfo && orderedDetailinfo?.length > 0) {
      for (const info of orderedDetailinfo) {
        childQuestions = detailinfo?.filter((item) => {
          const matchingRules = surveyBranchLogic?.filter(
            (inneritem) => inneritem.QuestionId === info.QuestionId
          );
          if (!matchingRules || matchingRules.length === 0) return false;

          return matchingRules.some((rule: any) =>
            rule.NextSurveyQuestionId === (item as any).QuestionId ||
            rule.NextSurveyQuestionId === (item as any).SurveySectionQuestionId ||
            (rule.NextQuestionId && rule.NextQuestionId === (item as any).QuestionId)
          );
        });

        const Qindex = orderedDetailinfo?.findIndex(
          (item) => item.QuestionId === info.QuestionId
        );

        orderedDetailinfo = insertArrayAtIndex(
          orderedDetailinfo,
          childQuestions,
          Qindex + 1
        );

        detailinfo = detailinfo?.filter(
          (item) => !childQuestions?.includes(item)
        );
      }
    } else {
      const MostParentQuestion: DetailedInfo[] = detailinfo?.filter(
        (item1) =>
          !surveyBranchLogic?.some(
            (item2) =>
              item2.NextSurveyQuestionId === (item1 as any).QuestionId ||
              item2.NextSurveyQuestionId === (item1 as any).SurveySectionQuestionId ||
              (item2.NextQuestionId && item2.NextQuestionId === (item1 as any).QuestionId)
          )
      );
      orderedDetailinfo = [...orderedDetailinfo, ...MostParentQuestion];
      detailinfo = detailinfo?.filter(
        (item) => !MostParentQuestion?.includes(item)
      );
    }

    // Safety check: Avoid infinite recursion if no progress made
    if (detailinfo.length === initialLength) {
      console.warn("Infinite recursion detected in survey layout, returning current progress");
      return [...orderedDetailinfo, ...detailinfo];
    }

    return await getOrderedDetailInfoRecursive(
      detailinfo,
      orderedDetailinfo,
      surveyBranchLogic
    );
  };

  const getVisibleAndAnsweredQuestions = (
    questions: DetailedInfo[],
    watch: UseFormWatch<any>
  ): { visible: number; answered: number } => {
    let visibleCount = 0;
    let answeredCount = 0;

    const traverse = (items: DetailedInfo[]) => {
      for (const q of items) {
        const parentQuestionId = q?.BranchQuestionId;
        const expectedOptionId = q?.QuestionResponseOptionId;
        const isStarBranch = (q as any)?.IsStarBranch;
        const parentVal = parentQuestionId
          ? watch(`question_${parentQuestionId}`)
          : null;

        let isVisible = !parentQuestionId;
        if (parentQuestionId && parentVal) {
          if (isStarBranch && expectedOptionId) {
            const triggerValues = expectedOptionId.toString().split(",");
            isVisible = triggerValues.includes(parentVal?.toString());
          } else {
            isVisible = parentVal == expectedOptionId?.toString();
          }
        }

        if (!isVisible) continue;

        visibleCount++;

        const responseType =
          q?.SurveyQuestionResponseDetails?.[0]?.ResponseTypeName;
        const matrixFields = q?.MatrixFieldDetails;

        let isAnswered = false;

        if (responseType === "MatrixTypeResponse") {
          if (Array.isArray(matrixFields)) {
            isAnswered = matrixFields?.every(
              (f) => !!watch(`matrix_${f?.FieldId}`)
            );
          }
        } else if (responseType === "Priority Ranking") {
          if (Array.isArray(matrixFields)) {
            isAnswered = matrixFields?.every(
              (f) => !!watch(`priority_${f?.FieldId}`)
            );
          }
        } else {
          const value = watch(`question_${q?.QuestionId}`);
          isAnswered = Array.isArray(value)
            ? value?.length > 0
            : value !== null && value !== undefined && value !== "";
        }

        if (isAnswered) answeredCount++;

        if (q?.SurveyQuestionBranchResponseDetails?.length) {
          traverse(q?.SurveyQuestionBranchResponseDetails);
        }
      }
    };

    traverse(questions);

    return { visible: visibleCount, answered: answeredCount };
  };

  const fetchSurveySections = async () => {
    setLoading(true);
    try {
      const userIddecrypted = assignUserId ? await decrypt(assignUserId) : null;
      const response: any = await axiosPost(getSurveySectionsDetails, {
        SurveyFormId: surveyFormId,
        SearchText: "",
        IsActive: true,
        CurrentPageNo: 1,
        PageSize: 0,
        SortDir: "ASC",
        SortBy: "SurveyFormId",
        usersId: userIddecrypted ?? null,
      });
      if (response?.data?.Status === 200) {
        const ExtraData = response?.data?.ExtraData ?? null;
        const surveyBranchLogic =
          ExtraData?.surveyQuestionBranchLogicLists ??
          ExtraData?.SurveyQuestionBranchLogicLists ??
          ExtraData?.surveyQuestionBranchLogicList ??
          ExtraData?.SurveyQuestionBranchLogicList ??
          [];

        let sectionsRaw = Array.isArray(ExtraData)
          ? ExtraData
          : ExtraData?.surveySectionsLists ??
          ExtraData?.SurveySectionsLists ??
          ExtraData?.surveySectionsList ??
          ExtraData?.SurveySectionsList ??
          [];

        // If ExtraData itself looks like a list item (has DetailedInfo) or no sections found yet
        if (sectionsRaw.length === 0 && ExtraData?.DetailedInfo) {
          sectionsRaw = [ExtraData];
        }

        const toArray = (value: any): any[] => {
          if (Array.isArray(value)) return value;
          if (!value) return [];
          return [value];
        };

        const parseMaybeJsonArray = (value: any): any[] => {
          if (Array.isArray(value)) return value;
          if (typeof value === "string") {
            try {
              const parsed = JSON.parse(value);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        };

        const normalizeSections = (list: any[]): any[] =>
          toArray(list).map((section: any) => ({
            ...section,
            DetailedInfo: parseMaybeJsonArray(
              section?.DetailedInfo ??
              section?.detailedInfo
            ),
            SurveySubSection:
              normalizeSections(
                section?.SurveySubSection ??
                section?.surveySubSection ??
                []
              ),
          }));
        const sections = normalizeSections(sectionsRaw);
        for (const s of sections) {
          const originalDetailedInfo = Array.isArray(s?.DetailedInfo) ? s.DetailedInfo : [];
          let orderedDetailinfo: DetailedInfo[] =
            await getOrderedDetailInfoRecursive(
              originalDetailedInfo,
              [],
              surveyBranchLogic
            );
          s.DetailedInfo = (orderedDetailinfo && orderedDetailinfo.length > 0)
            ? orderedDetailinfo
            : originalDetailedInfo;
        }
        const allSectionsTotalQuestions = sections.reduce(
          (sum: number, { CombinedCount = 0 }: DetailedInfo) =>
            sum + CombinedCount,
          0
        );
        if (sections[0]?.TimeLimit) {
          setAlertBeforeTime(hhmmToSeconds(sections[0]?.AlertBeforeTime));
          setDurationInMinutes(hhmmToSeconds(sections[0]?.TimeLimit));
          setFormType(sections[0]?.FormType);
        }
        setSurveySections(sections);
        setSurveyFormTitle(
          sections[0]?.SurveyFormTitle ??
          sections[0]?.surveyFormTitle ??
          ""
        );
        setThankYouMessage(
          sections[0]?.ThankYouMessage ??
          sections[0]?.thankYouMessage ??
          ""
        );
        setIsCaptchaEnabled(
          sections[0]?.IsActiveInConfigDetail ??
          sections[0]?.isActiveInConfigDetail
        );
        setTotalQuestions(allSectionsTotalQuestions);
        setSurveyFormInstructions(
          sections[0]?.SurveyFormInstructions ??
          sections[0]?.surveyFormInstructions ??
          ""
        );
        if (assignUserId) {
          const userIdProp = await decrypt(assignUserId);
          fetchSurveyResponses(userIdProp);
        }
      } else {
        console.error("Error fetching survey sections");
      }
    } catch (error) {
      console.error("Error fetching survey sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyResponses = async (userIdProp?: number) => {
    setLoading(true);

    try {
      const surveysid = await decrypt(surveyIdFromParams);
      const response: any = await axiosPost(getAllSurveyResponses, {
        SurveyId: surveysid,
        SearchText: "",
        IsActive: true,
        CurrentPageNo: 1,
        PageSize: 100,
        SortDir: "ASC",
        SortBy: "SurveyResponseId",
        AssignedUserId: userIdProp,
        usersId: userIdProp,
      });

      if (response?.data?.Status === 200) {
        const surveyResponses: SurveyResponse[] = response?.data?.ExtraData;
        if (surveyResponses?.length > 0) {
          setShowSubmitButton(false);
          setWelcomePageDemo(true);

          const hasDraft = surveyResponses.some((res) => res.IsDraft);
          if (hasDraft) {
            setIsDraft(true);
            setShowSubmitButton(true);
            setShowProgress(true);
          }

          surveyResponses?.forEach((surveyResponse) => {
            const {
              QuestionId,
              SelectedResponseText,
              SelectedResponseValue,
              SelectedStarResponseValue,
              FieldId,
              IsDraft,
            } = surveyResponse;
            // Handle matrix fields
            if (
              FieldId &&
              surveyResponse?.ResponseTypeName === "MatrixTypeResponse"
            ) {
              setValue(`matrix_${FieldId}`, SelectedResponseValue);
            }

            if (
              FieldId &&
              surveyResponse?.ResponseTypeName === "Priority Ranking"
            ) {
              setValue(`priority_${FieldId}`, SelectedResponseValue);
            }

            // Handle general questions
            if (SelectedResponseText) {
              setValue(`question_${QuestionId}`, SelectedResponseText);
            } else if (SelectedResponseValue) {
              if (SelectedResponseValue?.includes(",")) {
                setValue(
                  `question_${QuestionId}`,
                  SelectedResponseValue.split(",") // Split for multi-select values
                );
              } else {
                setValue(`question_${QuestionId}`, SelectedResponseValue); // Single value
              }
            } else if (SelectedStarResponseValue) {
              setValue(`question_${QuestionId}`, SelectedStarResponseValue);
            }

            // Track draft status
            if (IsDraft) {
              setIsDraft(true);
            }
          });
        }
      } else {
        console.error("Error fetching responses");
      }
    } catch (error) {
      console.error("Error fetching responses", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (surveyFormId) {
      fetchSurveySections();
    }
    if (!assignUserId || assignUserId === undefined) {
      setApplyValidation(false);

      setShowProgress(false);
    }
  }, [surveyFormId]);

  const getSubmitPayload = async (
    surveySections: SurveySection[],
    data: Record<string, any>,
    responsedata: any[]
  ): Promise<any[]> => {
    if (!responsedata) {
      responsedata = [];
    }
    for (const section of surveySections ?? []) {
      let sectionId = section.SurveySectionId;

      if (section?.DetailedInfo) {
        for (const question of section?.DetailedInfo ?? []) {
          let questionId = question.QuestionId;

          const responseId = data[`question_${questionId}`];
          if (question?.SurveyQuestionResponseDetails) {
            for (const questionResponseDetail of question?.SurveyQuestionResponseDetails ??
              []) {
              let questionResponseId =
                questionResponseDetail.QuestionResponseId;
              let responseTypeName = questionResponseDetail.ResponseTypeName;

              let selectedResponseText = "";
              let selectedResponseValue = "";
              let selectedStarResponseValue = "";

              if (
                responseTypeName === "CheckBox" ||
                responseTypeName === "Multi-Select DropDown"
              ) {
                selectedResponseValue = Array.isArray(responseId)
                  ? responseId.join(",")
                  : responseId ?? "";
              } else if (
                responseTypeName === "DropDown" ||
                responseTypeName === "Radio Button" ||
                responseTypeName === "Yes or No" ||
                responseTypeName === "Single Choice"
              ) {
                selectedResponseValue = responseId;
              } else if (
                responseTypeName === "TextBox" ||
                responseTypeName === "TextArea" ||
                responseTypeName === "Text Area" ||
                responseTypeName === "DatePicker" ||
                responseTypeName === "Rich Text Editor"
              ) {
                selectedResponseText = responseId;
              } else if (
                responseTypeName === "3 Star Rating" ||
                responseTypeName === "5 Star Rating" ||
                responseTypeName === "10 Star Rating" ||
                responseTypeName === "3 Star Rating With N/A" ||
                responseTypeName === "5 Star Rating With N/A" ||
                responseTypeName === "10 Star Rating With N/A" ||
                responseTypeName === "Smiley" ||
                responseTypeName === "NPS" ||
                responseTypeName === "ThumbsUpDown"
              ) {
                selectedStarResponseValue = responseId?.toString();
              } else {
              }

              // Handle matrix response type
              if (responseTypeName === "MatrixTypeResponse") {
                // Iterate through the data to extract matrix keys
                Object.entries(data).forEach(([key, value]) => {
                  if (key.startsWith("matrix_") && value) {
                    const [, fieldId, optionId] = key.split("_"); // Extract fieldId and optionId from key

                    // Create the matrix response object
                    const matrixResponseObj = {
                      SurveySectionId: sectionId,
                      QuestionId: questionId,
                      QuestionResponseId: questionResponseId,
                      SelectedResponseText: "",
                      SelectedResponseValue: value.toString(), // Assign the matrix value
                      SelectedStarResponseValue: "",
                      FieldId: parseInt(fieldId, 10), // Convert fieldId to a number
                      OptionId: parseInt(optionId ?? 0, 10), // Convert optionId to a number
                    };

                    responsedata?.push(matrixResponseObj);
                  }
                });
              } else if (responseTypeName === "Priority Ranking") {
                Object.entries(data)?.forEach(([key, value]) => {
                  if (key.startsWith("priority_") && value) {
                    const [, fieldId, optionId] = key.split("_");

                    const matrixResponseObj = {
                      SurveySectionId: sectionId,
                      QuestionId: questionId,
                      QuestionResponseId: questionResponseId,
                      SelectedResponseText: "",
                      SelectedResponseValue: value?.toString(),
                      SelectedStarResponseValue: "",
                      FieldId: parseInt(fieldId, 10),
                      OptionId: parseInt(optionId ?? 0, 10),
                    };
                    responsedata.push(matrixResponseObj);
                  }
                });
              } else {
                // Handle non-matrix response types
                const questionKey = `question_${questionId}`;
                const responseValue = data[questionKey];
                if (
                  responseValue &&
                  (!Array.isArray(responseValue) || responseValue?.length > 0)
                ) {
                  const nonMatrixResponseObj = {
                    SurveySectionId: sectionId,
                    QuestionId: questionId,
                    QuestionResponseId: questionResponseId,
                    SelectedResponseText: selectedResponseText,
                    SelectedResponseValue: selectedResponseValue,
                    SelectedStarResponseValue: selectedStarResponseValue,
                    FieldId: 0,
                    OptionId: 0,
                  };

                  responsedata.push(nonMatrixResponseObj);
                }
              }
            }
          }
        }
      }

      if (section.SurveySubSection) {
        await getSubmitPayload(section.SurveySubSection, data, responsedata);
      }
    }

    return responsedata;
  };
  useEffect(() => {
    let totalVisible = 0;
    let totalAnswered = 0;

    surveySections?.forEach((section) => {
      const { answered, visible } = getVisibleAndAnsweredQuestions(
        section?.DetailedInfo || [],
        watch
      );
      totalVisible += visible;
      totalAnswered += answered;
    });

    setTotalQuestions(totalVisible);
    setCurrentCount(totalAnswered);
  }, [watchedValues, surveySections]);

  const getVariant = (percentage: number) => {
    if (percentage < 50) return "danger";
    if (percentage < 80) return "warning";
    return "success";
  };

  const handleFinalSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (!assignUserId) {
      return;
    }
    let token = null;

    if (isCaptchaEnabled) {
      try {
        token = await handleCaptcha();

        if (!token) {
          showToast(
            "CAPTCHA validation failed. Please try again.",
            ToastTypeCode?.ERROR
          );
          return;
        }
      } catch (error) {
        showToast(
          "An error occurred during reCAPTCHA verification.",
          ToastTypeCode?.ERROR
        );
        return;
      }
    }

    let questionAnsList: SurveySection[] = await getSubmitPayload(
      surveySections,
      data,
      []
    );

    if (!isDraft && questionAnsList?.length === 0) {
      showToast("No data to Save", ToastTypeCode?.ERROR);
      return;
    }

    let userIddecrypted = await decrypt(assignUserId);
    const payload = {
      SurveyId: surveyid,
      SurveyFormId: surveyFormId,
      AssignUserID: usersId ? usersId : null,
      questionAnsList: questionAnsList,
      usersId: usersId ? usersId : userIddecrypted,
      IsDraft: false,
      ResponseEndDate: convertToUTC(getCurrentDate()),
      ResponseStartDate: responseStartDate ? convertToUTC(responseStartDate) : null,
      IsTimeOver: data?.TimeOver ? data?.TimeOver : false,
      recaptchaToken: token,
    };

    setLoading(true);

    try {
      const response: any = await axiosPost(insertSurveyResponse, payload);
      if (response.data?.Status !== 200) {
        if (
          response?.data?.ErrorMessage?.startsWith(
            "Your response saved as Draft"
          )
        ) {
          navigate("/dashboard");
        }
        showToast(response?.data?.ErrorMessage, ToastTypeCode?.ERROR);
      } else {
        showToast("Form submitted successfully", ToastTypeCode?.SUCCESS);
        clearDraft();
        if (!data?.TimeOver) {
          if (formType === 3) {
            setPollResultView(true);
          } else {
            navigate(`/thankyou`, {
              state: {
                message: thankYouMessage,
                isEmbedded: isEmbedded,
                surveyAssignmentId: response?.data?.ExtraData?.SurveyAssignmentId || surveysAssignmentId,
                userId: userId,
                isPublicSurvey: isPublicSurvey,
                formType: formType,
                responseGroupGuid: response?.data?.ExtraData?.ResponseGroupGuid
              },
            });
          }
        }
      }
    } catch (error) {
      showToast("Error submitting the Form", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDraftSubmit = async (watchAllFields: any, isDraft: boolean) => {
    if (!assignUserId) {
      return;
    }

    let token = null;

    if (isCaptchaEnabled) {
      try {
        token = await handleCaptcha();

        if (!token) {
          showToast(
            "CAPTCHA validation failed. Please try again.",
            ToastTypeCode?.ERROR
          );
          return;
        }
      } catch (error) {
        showToast(
          "An error occurred during reCAPTCHA verification.",
          ToastTypeCode?.ERROR
        );
        return;
      }
    }
    let questionAnsList: SurveySection[] = await getSubmitPayload(
      surveySections,
      watchAllFields,
      []
    );
    if (questionAnsList?.length === 0) {
      showToast("No data to Save", ToastTypeCode?.ERROR);
      return;
    }

    let userIddecrypted = await decrypt(assignUserId);

    const payload = {
      SurveyId: surveyid,
      SurveyFormId: surveyFormId,
      AssignUserID: usersId ? usersId : null,
      questionAnsList: questionAnsList,
      usersId: userIddecrypted,
      IsDraft: isDraft,
      recaptchaToken: token,
      ResponseEndDate: convertToUTC(getCurrentDate()),
      ResponseStartDate: responseStartDate ? convertToUTC(responseStartDate) : null
    };

    setLoading(true);
    try {
      const response: any = await axiosPost(insertSurveyResponse, payload);
      if (response?.data.Status !== 200) {
        showToast(response?.data?.ErrorMessage, ToastTypeCode?.ERROR);
      } else {
        if (isDraft) {
          showToast("Draft saved successfully", ToastTypeCode?.SUCCESS);
          navigate("/dashboard");
        } else {
          showToast("Form submitted successfully", ToastTypeCode?.SUCCESS);
          clearDraft();
          navigate(`/thankyou`, {
            state: {
              message: thankYouMessage,
              isEmbedded: isEmbedded,
              surveyAssignmentId: response?.data?.ExtraData?.SurveyAssignmentId || surveysAssignmentId,
              userId: userId,
              isPublicSurvey: isPublicSurvey,
              formType: formType,
              responseGroupGuid: response?.data?.ExtraData?.ResponseGroupGuid
            },
          });
        }
      }
    } catch (error) {
      showToast("Error submitting the Form", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
      setIsDraft(false);
    }
  };

  const generatePdf = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageHeight = pdf.internal.pageSize.height;
    const pageWidth = pdf.internal.pageSize.width;

    for (let i = 0; i < surveySections?.length; i++) {
      setCurrentRecord(i);
      await new Promise((resolve) => setTimeout(resolve, 100));

      const pageContent = document.querySelector(
        ".surveypreview-wrapper2"
      ) as HTMLElement | null;
      if (!pageContent) {
        console.error("Content not found!");
        continue;
      }

      pageContent.style.maxWidth = "800px";
      pageContent.style.margin = "10px";

      const canvas = await html2canvas(pageContent, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgWidth = pageWidth;
      const imgHeight = (canvas?.height * imgWidth) / canvas?.width;
      const totalHeight = canvas?.height;

      let currentY = 0;

      while (currentY < totalHeight) {
        const sliceCanvas = document.createElement("canvas");
        const sliceContext = sliceCanvas?.getContext("2d");

        const sliceHeight = Math.min(
          totalHeight - currentY,
          pageHeight * (canvas?.height / imgHeight)
        );
        sliceCanvas.width = canvas?.width;
        sliceCanvas.height = sliceHeight;

        if (sliceContext) {
          sliceContext?.drawImage(
            canvas,
            0,
            currentY,
            canvas?.width,
            sliceHeight,
            0,
            0,
            canvas?.width,
            sliceHeight
          );
        }

        const sliceImgData = sliceCanvas?.toDataURL("image/png");
        const slicePdfHeight = (sliceHeight * imgWidth) / canvas?.width;

        if (currentY === 0 && i === 0) {
          pdf.addImage(sliceImgData, "PNG", 0, 0, imgWidth, slicePdfHeight);
        } else {
          pdf.addPage();
          pdf.addImage(sliceImgData, "PNG", 0, 0, imgWidth, slicePdfHeight);
        }

        currentY += sliceHeight;
      }
      pageContent.style.maxWidth = "";
      pageContent.style.margin = "";
    }
    pdf.save(`${surveyFormTitle}.pdf`);
    window.location.reload();
  };

  const fetchPollLiveResult = async () => {
    const payload = {
      SurveyAssignmentId: surveysAssignmentId,
      SurveyFormId: surveyFormId,
    };
    setLoading(true);
    try {
      const response: any = await axiosPost(getPollResponse, payload);
      if (response?.data?.Status !== 200) {
        showToast(response?.data?.ErrorMessage, ToastTypeCode?.ERROR);
      } else {
      }
    } catch (error) {
      showToast("Error submitting the Form", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }

    setPollResultView(true);
  };

  const handleNext = async () => {
    if (userId && surveyId) {
      const valid = await methods.trigger();
      if (valid) {
        setCurrentRecord((prevStep) => prevStep + 1);
        scrollRef.current?.scrollTo(0, 0);
      }
      else {
        setSubmitButtonClicked((c) => c + 1);
      }
    } else {
      setCurrentRecord((prevStep) => prevStep + 1);
      scrollRef.current?.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    if (durationInMinutes !== undefined) {
      setTimeLeft(durationInMinutes - timeDifferenceAgainLogin);
    }
  }, [durationInMinutes, timeDifferenceAgainLogin]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours}:${minutes < 10 ? "0" : ""}${minutes}:${remainingSeconds < 10 ? "0" : ""
      }${remainingSeconds}`;
  };

  const onSubmitClicked = () => {
    setIsDraft(false);
    setShowModal(false);

    handleSubmit(handleFinalSubmit, () =>
      setSubmitButtonClicked((c) => c + 1)
    )();
  };

  const progressPercentage =
    totalquestions > 0 ? Math.round((currentCount / totalquestions) * 100) : 0;

  return (
    <>
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Submit</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Would you like to submit the form?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handlePreview}>
            Cancel
          </Button>
          {currentRecord === surveySections?.length - 1 && showSubmitButton && (
            <Button
              variant="success"
              className="mx-2"
              onClick={onSubmitClicked}
            >
              Submit
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {isNotAccepting ? (
        <NotAcceptingForm isEmbedded={isEmbedded} />
      ) : !welcomePageDemo && userId && surveyId ? (
        <WelcomePage
          SurveyFormTitle={surveyFormTitle}
          SurveyFromInstructions={surveyFormInstructions}
          setWelcomePageDemo={setWelcomePageDemo}
          formType={formType}
        />
      ) : (
        <div className="surveypreview-wrapper" ref={scrollRef}>
          <Container className="vh-100 align-items-center justify-content-center">

            <Card>

              <Card.Header className="p-0">
                <div className="bg-white text-center py-2 form-logo">{!isEmbedded && orgId && (
                  <Row>
                    <Col lg={12}>
                      <OrganizationLogo orgId={orgId} />
                    </Col>
                  </Row>
                )}</div>
                <div className="survey-title p-3">

                  <Row>
                    <Col lg={6}>
                      <h2 className="mb-0">{surveyFormTitle}</h2>
                    </Col>
                    <Col lg={6}>
                      <div className="d-flex justify-content-end question-actions">
                        {welcomePageDemo &&
                          userId &&
                          surveyId &&
                          showSubmitButton === true &&
                          formType === 2 && (
                            <div
                              className={`alert my-1 me-2 ${timeLeft !== undefined &&
                                alertBeforeTime !== undefined &&
                                timeLeft > alertBeforeTime
                                ? "alert-success"
                                : "alert-danger"
                                }`}
                            >
                              Time Left:{" "}
                              <span
                                className={`badge ms-2 ${timeLeft !== undefined &&
                                  alertBeforeTime !== undefined &&
                                  timeLeft > alertBeforeTime
                                  ? "badge-success"
                                  : "badge-danger"
                                  }`}
                              >
                                {timeLeft !== undefined
                                  ? formatTime(timeLeft)
                                  : "00:00:00"}
                              </span>
                            </div>
                          )}
                        {formType !== 3 && (
                          <>
                            {(!showProgress || showSubmitButton !== true) && (
                              <Button
                                type="button"
                                className="me-2"
                                onClick={generatePdf}
                              >
                                Generate PDF
                              </Button>
                            )}
                            {assignUserId && isPublicSurvey === false && formType === 1 && (
                              <Button
                                type="button"
                                variant="info"
                                className="mx-2"
                                onClick={() => {
                                  setIsDraft(true);
                                  const draftData = getValues();
                                  handleDraftSubmit(draftData, true);
                                }}
                              >
                                <img
                                  src={savedraftico}
                                  className="me-2"
                                  width="14"
                                  height="14"
                                  alt="Save Draft"
                                />
                                Save Draft
                              </Button>
                            )}
                          </>
                        )}
                        {!isEmbedded && (
                          <Button
                            className="btn-primary"
                            onClick={() => navigate("/dashboard")}
                          >
                            <img
                              src={dashboardico}
                              className="me-2"
                              width="16"
                              height="16"
                              alt="Dashboard"
                            />
                            Go To Dashboard
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card.Header>
              <Card.Body>
                {showProgress &&
                  showSubmitButton === true &&
                  !pollResultView && (
                    <>
                      <div className="progress-info">
                        <span>{currentCount}</span> /{" "}
                        <span>{totalquestions}</span>
                      </div>
                      <div className="progress-container">
                        <div className="col-md-12">
                          <div className="dashb-card">
                            <ProgressBar
                              striped
                              variant={getVariant(progressPercentage)}
                              now={progressPercentage}
                              label={`${progressPercentage}%`}
                              className="pg-block"
                            />
                            <p>Question Completion Rate</p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                <Row>
                  <Col>
                    {surveySections?.length > 0 && (
                      <>
                        {loading ? (
                          <Loader />
                        ) : formType === 3 ? (
                          <>
                            {pollResultView === false ? (
                              <>
                                <div className="surveypreview-wrapper2">
                                  <PollView
                                    register={register}
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    RecursiveInnerSurveySections={
                                      surveySections[0]?.DetailedInfo
                                    }
                                    IsSubSection={false}
                                    showSubmitButton={showSubmitButton}
                                    setValue={setValue}
                                    currentRecord={currentRecord}
                                    questionCounter={1}
                                    isDraft={isDraft}
                                    submitButtonClicked={false}
                                  />
                                </div>
                                {showSubmitButton && (
                                  <Button
                                    variant="success"
                                    type="submit"
                                    className="mx-2"
                                    onClick={() => {
                                      setIsDraft(false);
                                      handleSubmit(handleFinalSubmit)();
                                      setSubmitButtonClicked(
                                        submitButtonClicked + 1
                                      );
                                    }}
                                    disabled={!applyValidation}
                                  >
                                    Submit
                                  </Button>
                                )}
                                <Button
                                  onClick={fetchPollLiveResult}
                                  className="mx-2 btn-primary"
                                >
                                  Live result
                                </Button>
                              </>
                            ) : (
                              <PollResult
                                setPollResultView={setPollResultView}
                                surveysAssignmentId={surveysAssignmentId}
                                surveyFormId={surveyFormId}
                              />
                            )}
                          </>
                        ) : (
                          <FormProvider {...methods}>
                            <Form onSubmit={handleSubmit(handleFinalSubmit)}>
                              <div className="surveypreview-wrapper2">
                                <RecursiveDetailSurveyForm
                                  register={register}
                                  control={control}
                                  errors={errors}
                                  watch={watch}
                                  recursiveSurveySections={
                                    surveySections[currentRecord]
                                  }
                                  IsSubSection={false}
                                  showSubmitButton={showSubmitButton}
                                  setValue={setValue}
                                  currentRecord={currentRecord}
                                  questionCounter={1}
                                  isDraft={isDraft}
                                  submitButtonClicked={submitButtonClicked}
                                />
                              </div>
                              <div className="text-center">
                                {currentRecord > 0 && (
                                  <Button
                                    variant="secondary"
                                    onClick={() => {
                                      setCurrentRecord(
                                        (prevStep) => prevStep - 1
                                      );
                                      scrollRef.current?.scrollTo(0, 0);
                                    }}
                                    className="mx-2"
                                  >
                                    Previous
                                  </Button>
                                )}

                                {currentRecord < surveySections?.length - 1 && (
                                  <Button
                                    variant="primary"
                                    onClick={handleNext}
                                    className="mx-2"
                                  >
                                    Next
                                  </Button>
                                )}

                                {currentRecord === surveySections?.length - 1 &&
                                  showProgress &&
                                  showSubmitButton === true && (
                                    <Button
                                      variant="primary"
                                      type="button"
                                      className="btn-primary mx-2"
                                      onClick={handleShowModal}
                                    >
                                      Save
                                    </Button>
                                  )}
                              </div>
                            </Form>
                          </FormProvider>
                        )}
                      </>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Container>
        </div>
      )}

      <Modal show={showKeyModal} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Access Key Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Please enter the access key to continue to the survey</Form.Label>
            <Form.Control
              type="text"
              value={accessKey}
              onChange={(e) => {
                setAccessKey(e.target.value);
                setKeyError("");
              }}
              isInvalid={!!keyError}
            />
            <Form.Control.Feedback type="invalid">
              {keyError}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={validateAccessKey} disabled={!accessKey || loading}>
            {loading ? "Validating..." : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};
export default DetailSurveyForm;
